<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row dense align="center" class="mb-3">
      <v-col cols="auto">
        <div class="caption grey--text text--darken-1" v-if="entry">
          {{ entry.course_code }} — {{ formatDate(entry.webinar_date) }} <br>
          <span v-if="entry.from_time && entry.to_time" class="grey--text text--darken-1">
             ({{
            formatTime(entry.from_time)
          }}
          - {{ formatTime(entry.to_time) }})
          </span> <br>
          <v-chip small v-if="published" class="primary ml-2">Published</v-chip>
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="12" md="2" v-if="isMultiCourse">
        <v-select
          v-model="courseFilter"
          :items="entryCourseCodes"
          label="Course code"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-filter-variant"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="search"
          label="Search USN"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
        />
      </v-col>
      <v-btn
        color="accent white--text"
        class="mr-2"
        :disabled="!webinarDateReached"
        @click="downloadReport"
      >
        <v-icon left>mdi-download</v-icon>
        Download
      </v-btn>
      <v-btn
        class="mr-2"
        color="accent white--text"
        :disabled="!webinarDateReached"
        @click="openAttendanceDialog"
      >
        <v-icon left>mdi-upload</v-icon>
        Upload
      </v-btn>
      
      <v-btn
        v-if="!published && !isReadOnly"
        color="accent white--text"
        :disabled="!webinarDateReached || notUploadedCount > 0 || loading"
        @click="openPublishDialog"
      >
        Publish
      </v-btn>
    </v-row>

    <v-row dense class="mb-2" v-if="!loading">
      <v-col cols="auto">
        <v-chip class="white--text" small color="grey darken-3"
          >Total: {{ rows.length }}</v-chip
        >
      </v-col>
      <v-col cols="auto">
        <v-chip class="white--text" small color="success darken-4"
          >Present: {{ presentCount }}</v-chip
        >
      </v-col>
      <v-col cols="auto">
        <v-chip class="white--text" small color="error darken-4"
          >Absent: {{ absentCount }}</v-chip
        >
      </v-col>
      <v-col cols="auto">
        <v-chip class="white--text" small color="grey darken-2"
          >Not Uploaded: {{ notUploadedCount }}</v-chip
        >
      </v-col>
    </v-row>

    <v-sheet outlined>
      <v-data-table
        :headers="headers"
        :items="filteredRows"
        :loading="loading"
        :search="search"
        fixed-header
        item-key="usn_id"
      >
        <template v-slot:no-data>
          <div class="text-center">No attendance records found</div>
        </template>

        <template #[`item.webinar_date`]="{ item }">
          {{ formatDate(item.webinar_date) }}
        </template>

        <template #[`item.inserted_at`]="{ item }">
          {{ formatDateTimeIST(item.inserted_at) }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip
            small
            :color="
              item.present === true
                ? 'success darken-4'
                : item.present === false
                ? 'error darken-4'
                : 'grey darken-2'
            "
            class="white--text"
          >
            {{
              item.present === true
                ? "Present"
                : item.present === false
                ? "Absent"
                : "Not Uploaded"
            }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            small
            @click="openEditDialog(item)"
            class="action-btn"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="editDialog" max-width="420" persistent>
      <v-card class="rounded-lg" elevation="24">
        <v-card-title
          class="primary white--text text-subtitle-1 font-weight-bold pa-4 pb-2"
        >
          <v-icon class="mr-2" color="white">mdi-account-edit</v-icon>
          Update Attendance
          <v-spacer />
          <v-btn icon small @click="closeEditDialog" class="close-btn">
            <v-icon color="white" small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <div
            class="d-flex align-center pa-3 mb-4 rounded-lg"
            :class="editItem.present ? 'bg-success-light' : 'bg-error-light'"
          >
            <v-avatar
              size="44"
              class="mr-3"
              :color="editItem.present ? 'success' : 'error'"
            >
              <span class="white--text font-weight-bold text-subtitle-2">
                {{ getInitials(editItem.name) }}
              </span>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="font-weight-medium text-subtitle-2">
                {{ editItem.name || "Unknown" }}
              </div>
              <div class="caption grey--text text--darken-1">
                {{ editItem.usn_id }}
              </div>
              <div class="caption grey--text text--darken-1">
                {{ editItem.email }}
              </div>
            </div>
            <v-chip
              :color="editItem.present ? 'success darken-4' : 'error darken-4'"
              small
              text-color="white"
              class="status-chip"
            >
              <v-icon x-small left>{{
                editItem.present ? "mdi-check-circle" : "mdi-close-circle"
              }}</v-icon>
              {{ editItem.present ? "Present" : "Absent" }}
            </v-chip>
          </div>

          <div
            class="status-section pa-3 rounded-lg"
            :class="editItem.present ? 'bg-success-light' : 'bg-error-light'"
          >
            <div class="d-flex align-center justify-space-between">
              <span
                class="caption font-weight-medium"
                :class="editItem.present ? 'success--text' : 'error--text'"
              >
                <v-icon x-small :color="editItem.present ? 'success' : 'error'"
                  >mdi-circle</v-icon
                >
                Current Status: {{ editItem.present ? "Present" : "Absent" }}
              </span>
            </div>

            <v-divider class="my-3" />

            <div class="text-center">
              <div class="caption grey--text mb-3">Change Status To</div>
              <v-btn-toggle
                v-model="editStatus"
                dense
                mandatory
                color="primary"
                group
                class="status-toggle"
              >
                <v-btn
                  :value="true"
                  :ripple="false"
                  class="status-btn present-btn"
                  :class="{ 'v-btn--active': editStatus === true }"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Present
                </v-btn>
                <v-btn
                  :value="false"
                  :ripple="false"
                  class="status-btn absent-btn"
                  :class="{ 'v-btn--active': editStatus === false }"
                >
                  <v-icon left small>mdi-close-circle</v-icon>
                  Absent
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-btn text color="grey darken-1" @click="closeEditDialog"
            >Cancel</v-btn
          >
          <v-spacer />
          <v-btn
            color="primary"
            :loading="saving"
            @click="saveEdit"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="attendanceDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          <v-icon small color="secondary" class="mr-2"
            >mdi-clipboard-check-outline</v-icon
          >
          Upload Attendance
          <v-spacer />
          <v-btn small icon @click="closeAttendanceDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5">
          <div class="caption grey--text text--darken-1 mb-3" v-if="entry">
            {{ entry.course_code }} — {{ formatDate(entry.webinar_date) }} ({{
              formatTime(entry.from_time)
            }}
            - {{ formatTime(entry.to_time) }})
          </div>
          <v-file-input
            v-model="attendanceFile"
            label="Attendance File *"
            outlined
            dense
            hide-details="auto"
            accept=".csv,.xlsx,.xls"
            prepend-icon=""
            show-size
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn
            class="accent white--text"
            :loading="uploadingAttendance"
            :disabled="!attendanceFile"
            @click="submitAttendance"
          >
            <v-icon left small>mdi-upload</v-icon>
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="publishDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          <v-icon small color="secondary" class="mr-2"
            >mdi-check-decagram</v-icon
          >
          Publish Attendance
          <v-spacer />
          <v-btn small icon @click="publishDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5">
          <div class="caption grey--text text--darken-1 mb-3" v-if="entry">
            {{ entry.course_code }} — {{ formatDate(entry.webinar_date) }}
          </div>
          <v-alert
            v-if="notUploadedCount > 0"
            type="warning"
            dense
            text
            class="mb-0"
          >
            {{ notUploadedCount }} student(s) still have pending attendance.
            Upload attendance for all students before publishing.
          </v-alert>
          <template v-else>
            <p class="body-2 mb-2">
              Are you sure you want to publish this attendance?
            </p>
            <v-alert type="info" dense text class="mb-0">
              Once published, students can see this attendance. Re-uploading
              will update it directly.
            </v-alert>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn text color="grey darken-1" @click="publishDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            class="primary white--text"
            :loading="publishing"
            :disabled="notUploadedCount > 0"
            @click="confirmPublish"
          >
            <v-icon left small>mdi-check-decagram</v-icon>
            Publish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      saving: false,
      entry: null,
      rows: [],
      search: "",
      attendanceDialog: false,
      uploadingAttendance: false,
      attendanceFile: null,
      published: false,
      publishDialog: false,
      publishing: false,
      editDialog: false,
      editStatus: true,
      courseFilter: null,
      editItem: {
        usn_id: "",
        name: "",
        email: "",
        present: null,
        course_code: "",
        webinar_date: "",
      },
    };
  },

  computed: {
    isReadOnly() {
      return this.$store.state.currentUser?.role === "Attendance Verifier";
    },
    entryId() {
      return this.$route.params.entryId;
    },
    entryCourseCodes() {
      if (!this.entry || !this.entry.course_code) return [];
      return String(this.entry.course_code)
        .split("/")
        .map((c) => c.trim())
        .filter((c) => c !== "");
    },
    isMultiCourse() {
      return this.entryCourseCodes.length > 1;
    },
    headers() {
      const cols = [
        { text: "USN", value: "usn_id" },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
      ];
      if (this.isMultiCourse) {
        cols.push({ text: "Course", value: "course_code" });
      }
      cols.push(
        { text: "Webinar Date", value: "webinar_date" },
        { text: "Student Status At", value: "inserted_at" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      );
      return cols;
    },
    filteredRows() {
      let rows = this.rows;
      if (this.isMultiCourse && this.courseFilter) {
        rows = rows.filter((r) => r.course_code === this.courseFilter);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter((r) => r.usn_id.toLowerCase().includes(q));
      }
      return rows;
    },
    presentCount() {
      return this.rows.filter((r) => r.present).length;
    },
    absentCount() {
      return this.rows.filter((r) => r.present === false).length;
    },
    notUploadedCount() {
      return this.rows.filter((r) => r.present === null).length;
    },
    webinarDateReached() {
      if (!this.entry || !this.entry.webinar_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const [y, m, d] = this.entry.webinar_date.split("-").map(Number);
      const webinarDate = new Date(y, m - 1, d);
      return webinarDate <= today;
    },
  },

  async created() {
    await this.loadReport();
    this.$store.commit("setBreadcrumbs", [
      { text: "Webinar Scheduler", to: { name: "Webinar Scheduler" } },
      {
        text: "Schedules",
        to: this.entry?.scheduler_run_id
          ? {
              path: `/webinar-scheduler/${this.entry.scheduler_run_id}/entries`,
            }
          : { name: "Webinar Scheduler" },
      },
      { text: "Attendance Report" },
    ]);
  },

  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          `/scheduler/entries/${this.entryId}/attendance_report`,
        );
        const data = res.data.data || res.data;
        this.entry = data.entry || null;
        this.published = Boolean(this.entry && this.entry.attendance_published);
        const students = data.students || data.attendance || [];
        this.rows = students.map((s) => ({
          ...s,
          course_code:
            s.course_code || (this.entry ? this.entry.course_code : ""),
          webinar_date: this.entry ? this.entry.webinar_date : s.webinar_date,
        }));
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Failed to load attendance report",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    openEditDialog(item) {
      this.editItem = { ...item };
      this.editStatus = item.present == null ? true : item.present;
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editItem = { usn_id: "", name: "", email: "", present: null };
      this.editStatus = true;
    },

    openAttendanceDialog() {
      this.attendanceFile = null;
      this.attendanceDialog = true;
    },

    closeAttendanceDialog() {
      this.attendanceDialog = false;
      this.attendanceItem = null;
      this.attendanceFile = null;
    },

    async submitAttendance() {
      if (!this.entry) return;
      this.uploadingAttendance = true;
      try {
        const fd = new FormData();
        fd.append("file", this.attendanceFile);
        await this.$axios.post(
          `/scheduler/entries/${this.entryId}/upload_attendance`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        this.$store.commit("showSnackbar", {
          message: "Attendance uploaded successfully",
          color: "success",
        });
        this.closeAttendanceDialog();
        await this.loadReport();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Attendance upload failed";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
        if (e.response?.data?.count > 0) await this.loadReport();
      } finally {
        this.uploadingAttendance = false;
      }
    },

    openPublishDialog() {
      this.publishDialog = true;
    },

    async confirmPublish() {
      if (this.notUploadedCount > 0) return;
      this.publishing = true;
      try {
        await this.$axios.post(
          `/scheduler/entries/${this.entryId}/publish_attendance`,
        );
        this.published = true;
        this.publishDialog = false;
        this.$store.commit("showSnackbar", {
          message: "Attendance published successfully",
          color: "success",
        });
        await this.loadReport();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to publish attendance";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.publishing = false;
      }
    },

    async saveEdit() {
      this.saving = true;
      try {
        await this.$axios.patch(
          `/scheduler/entries/${this.entryId}/attendance/${this.editItem.usn_id}`,
          { present: this.editStatus },
        );
        const idx = this.rows.findIndex(
          (r) => r.usn_id === this.editItem.usn_id,
        );
        if (idx !== -1) {
          this.$set(this.rows, idx, {
            ...this.rows[idx],
            present: this.editStatus,
          });
        }
        this.$store.commit("showSnackbar", {
          message: "Attendance updated successfully",
          color: "success",
        });
        this.closeEditDialog();
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Failed to update attendance",
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    goBack() {
      this.$router.back();
    },

    downloadReport() {
      const headers = "usn_id,name,email,status,student_status_at";
      const csvRows = this.filteredRows.map((r) =>
        [
          this.csvCell(r.usn_id),
          this.csvCell(r.name),
          this.csvCell(r.email),
          this.presentLabel(r.present),
          this.csvCell(this.statusAtLabel(r.inserted_at)),
        ].join(","),
      );

      const csv = "﻿" + [headers, ...csvRows].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const course =
        this.isMultiCourse && this.courseFilter
          ? this.courseFilter
          : this.entry
          ? this.entry.course_code
          : `entry${this.entryId}`;
      const date = this.entry ? this.entry.webinar_date : "";
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_template_${course}${date ? "_" + date : ""}.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // The table renders an em dash for a missing timestamp; a CSV cell is better
    // left empty, the same way presentLabel treats an unmarked student.
    statusAtLabel(value) {
      if (!value) return "";
      const formatted = this.formatDateTimeIST(value);
      return formatted === "—" ? "" : formatted;
    },

    presentLabel(present) {
      if (present === true) return "Present";
      if (present === false) return "Absent";
      return "";
    },

    csvCell(value) {
      const str = value == null ? "" : String(value);

      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}-${m}-${y}`;
    },

    formatTime(timeStr) {
      if (!timeStr) return "—";
      return timeStr.split(":").slice(0, 2).join(":");
    },

    formatDateTimeIST(value) {
      if (!value) return "—";

      let raw = String(value).trim().replace(" ", "T");
      if (!/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) raw += "Z";

      const date = new Date(raw);
      if (isNaN(date.getTime())) return "—";

      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
        .formatToParts(date)
        .reduce((acc, part) => {
          acc[part.type] = part.value;
          return acc;
        }, {});

      return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
    },
  },
};
</script>

<style scoped>
.rounded-lg {
  border-radius: 12px;
}

.primary-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #2196f3 100%);
}

.bg-success-light {
  background-color: #e8f5e9 !important;
  border: 1px solid #a5d6a7;
}

.bg-error-light {
  background-color: #fce4ec !important;
  border: 1px solid #ef9a9a;
}

.status-chip {
  font-weight: 500;
  border-radius: 20px;
  padding: 4px 12px;
}

.status-chip .v-icon {
  font-size: 16px;
}

.status-toggle .v-btn {
  min-width: 100px;
  text-transform: none !important;
  transition: all 0.3s ease;
}

.status-toggle .v-btn--active.present-btn {
  background-color: #4caf50 !important;
  color: white !important;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.status-toggle .v-btn--active.absent-btn {
  background-color: #f44336 !important;
  color: white !important;
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
}

.status-btn {
  transition: all 0.3s ease;
}

.status-btn:hover {
  transform: scale(1.02);
}

.status-btn:not(.v-btn--active):hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.status-section {
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.action-btn:hover {
  background-color: rgba(25, 118, 210, 0.08);
}

.save-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.v-card {
  transition: box-shadow 0.3s ease;
}

.v-dialog {
  animation: dialogFadeIn 0.3s ease;
}

@keyframes dialogFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 600px) {
  .status-toggle .v-btn {
    min-width: 80px;
    font-size: 12px;
    padding: 0 8px;
  }
}
</style>
